import { useJobs } from '../../jobs/hooks/useJobs';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, CheckCircle, User, Star } from 'lucide-react';
import { RolePermissions } from '../../users/hooks/useRoleAccess';
import { DateUtils } from '@cleanpro/common-ui';

interface User {
  id: string;
  email: string;
  role?: string;
  fullName?: string;
}

interface EmployeeDashboardContentProps {
  user: User;
  permissions: RolePermissions;
}

export default function EmployeeDashboardContent({ user, permissions }: EmployeeDashboardContentProps) {
  const { jobs } = useJobs();
  const navigate = useNavigate();

  // Filter jobs assigned to this employee
  const employeeJobs = jobs.filter(b => b.employeeId === user.id);
  const todayJobs = employeeJobs.filter(b => {
    const today = new Date().toDateString();
    return new Date(b.time).toDateString() === today;
  });
  const upcomingJobs = employeeJobs.filter(b => {
    const jobDate = new Date(b.time);
    const today = new Date();
    return jobDate > today;
  }).slice(0, 5);

  const stats = {
    todayJobs: todayJobs.length,
    upcomingJobs: upcomingJobs.length,
    completedThisMonth: employeeJobs.filter(b => {
      const jobDate = new Date(b.time);
      const now = new Date();
      return b.status === 'completed' && 
             jobDate.getMonth() === now.getMonth() && 
             jobDate.getFullYear() === now.getFullYear();
    }).length
  };

  return (
    <div className="space-y-6">
      {/* Employee Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Today's Jobs</p>
              <p className="text-3xl font-bold text-gray-900">{stats.todayJobs}</p>
            </div>
            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Calendar className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Upcoming Jobs</p>
              <p className="text-3xl font-bold text-gray-900">{stats.upcomingJobs}</p>
            </div>
            <div className="h-12 w-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completed This Month</p>
              <p className="text-3xl font-bold text-gray-900">{stats.completedThisMonth}</p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Today's Schedule */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-medium text-gray-800">Today's Schedule</h2>
        </div>
        <div className="p-6">
          {todayJobs.length > 0 ? (
            <div className="space-y-4">
              {todayJobs.map((job) => (
                <div key={job.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                     onClick={() => permissions.canViewJobs && navigate(`/jobs/${job.id}`)}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{job.serviceName}</h3>
                      <p className="text-sm text-gray-600">{job.customerName}</p>
                      <div className="flex items-center mt-1 text-sm text-gray-500">
                        <Clock className="h-4 w-4 mr-1" />
                        {job.time} ({job.duration}h)
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${
                        job.status === 'completed' ? 'bg-green-100 text-green-800' :
                        job.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {job.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No jobs scheduled for today</p>
            </div>
          )}
        </div>
      </div>

      {/* Upcoming Jobs */}
      {permissions.canViewJobs && upcomingJobs.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-medium text-gray-800">Upcoming Jobs</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {upcomingJobs.map((job) => (
                <div key={job.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                     onClick={() => navigate(`/jobs/${job.id}`)}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{job.serviceName}</h3>
                      <p className="text-sm text-gray-600">{job.customerName}</p>
                      <div className="flex items-center mt-1 text-sm text-gray-500">
                        <Calendar className="h-4 w-4 mr-1" />
                        {DateUtils.formatDateLong(job.time)} at {DateUtils.formatTimeFromDate(job.time)}
                      </div>
                      {job.rating && (
                        <div className="flex items-center mt-1">
                          <Star className="h-4 w-4 text-yellow-400 fill-current" />
                          <span className="text-sm text-gray-600 ml-1">{job.rating}/5</span>
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${
                        job.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {job.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-medium text-gray-800">Quick Actions</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {permissions.canViewSchedule && (
              <button
                onClick={() => navigate('/schedule')}
                className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
              >
                <Calendar className="h-8 w-8 text-indigo-600 mr-3" />
                <div className="text-left">
                  <h3 className="font-medium text-gray-900">View Schedule</h3>
                  <p className="text-sm text-gray-600">Check your upcoming appointments</p>
                </div>
              </button>
            )}
            
            {permissions.canViewProfile && (
              <button
                onClick={() => navigate('/profile')}
                className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
              >
                <User className="h-8 w-8 text-indigo-600 mr-3" />
                <div className="text-left">
                  <h3 className="font-medium text-gray-900">My Profile</h3>
                  <p className="text-sm text-gray-600">Update your information</p>
                </div>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
